import {Button, List, message, Modal, notification, Popover, Progress, Space} from "antd";
import React, {useContext, useEffect, useLayoutEffect, useRef, useState, useCallback} from "react";
import filesize from 'filesize';
import SparkMD5 from "spark-md5";
import sha256 from "js-sha256";
import Evaporate from "evaporatejs";
import {AppContext} from "../../contexts/AppContext";
import {useUploadsDispatch, useUploadsState} from "../../contexts/UploadsContext";
import {useAssetsDispatch} from "../../contexts/AssetsContext";
import api from "../api";
import {useAggsDispatch} from "../../contexts/AggsContext";
import useConsumer from "../../channels/consumer";
import {SessionContext} from "../../contexts/SessionContext";
import uuid from "../helpers/uuid";
import mapLimit from 'async/mapLimit';
import {useLoadedAssetsDispatch} from "../../contexts/LoadedAssetsContext";
import PauseOutlined from "@ant-design/icons/lib/icons/PauseOutlined";
import PlayCircleOutlined from "@ant-design/icons/lib/icons/PlayCircleOutlined";
import pluralize from "pluralize";
import {useEditAssetsDispatch, useEditAssetsState} from "../../contexts/EditAssetsContext";

import {
    BrowserView,
    MobileView,
    isBrowser,
    isMobile
} from "device-detect";
import {useAssetGroupDispatch, useAssetGroupState} from "../../contexts/AssetGroupContext";
import {useTranslation} from "react-i18next";
import {FileFilled, OrderedListOutlined} from "@ant-design/icons";
import VerticalSpace from "@/components/helpers/VerticalSpace";
import ListItem from "antd/es/upload/UploadList/ListItem";
import {clearBulkUploadSubscription, createBulkUploadLifecycle} from "./bulkUploadLifecycle";
import {activeUploadGuids, shouldStopForCancellation} from "./uploadTracking";
import useRequestRefreshScheduler from "../helpers/useRequestRefreshScheduler";
import {batchIsDeleted, batchIsTerminal, batchIsTransferring, batchNeedsRefresh, batchTransferredBytes} from './currentUploadBatches';
import UploadBatchDetails, {UploadBatchLink} from './UploadBatchDetails';
import installEvaporateSchedulerFix from './evaporateScheduler';

export default ({quickUpload})=> {
    const {state} = useContext(AppContext);
    const {jwt, currentUser} = state

    const {state: sessionState} = useContext(SessionContext);
    const {sessionGuid, currentOrg} = sessionState;

    const {uploads, uploadingAssets, stalledAssets = {}, networkOffline, transferRetry, fileAdds, disconnected, uploadState, currentBatches = {}, currentBatchScope} = useUploadsState();
    const uploadsDispatch = useUploadsDispatch()

    const assetsDispatch = useAssetsDispatch();
    const loadedAssetsDispatch = useLoadedAssetsDispatch()

    const aggsDispatch = useAggsDispatch();

    const editAssetDispatch = useEditAssetsDispatch()
    const {assetEdits} = useEditAssetsState();

    const lastProgressTimestamps = useRef({});
    const transferredBytesByAsset = useRef({});
    const uploadingAssetsRef = useRef([]);
    uploadingAssetsRef.current = uploadingAssets;
    const stalledAssetsRef = useRef({});
    stalledAssetsRef.current = stalledAssets;
    const pausedUploadGuids = useRef(new Set());
    const pauseOperations = useRef({});
    const pendingFinalizations = useRef(new Map());
    const finalizationRequests = useRef(new Set());
    const offlineRef = useRef(false);
    const lastStatsRefresh = useRef(0);
    const uploadStateRef = useRef({byGuid: {}});
    const evaporateIdsByUploadGuid = useRef({});
    const cancelledRevisionUploadGuids = useRef(new Set());
    const cancelledRevisionBulkUploadIds = useRef(new Set());
    const STALL_TIMEOUT = 60000; // 60 seconds
    const STATS_REFRESH_THROTTLE = 3000; // Refresh stats at most every 3 seconds

    const {currentUpload} = useAssetGroupState()
    const assetGroupDispatch = useAssetGroupDispatch();

    const batchStorageKey = `current-upload-batches:${currentOrg?.id}:${currentUser?.id}`;
    const batchStorageKeyRef = useRef();
    const batchesRef = useRef(currentBatches);
    batchesRef.current = currentBatchScope === batchStorageKey ? currentBatches : {};
    useLayoutEffect(() => {
        let batches = {};
        try { batches = JSON.parse(sessionStorage.getItem(batchStorageKey) || '{}'); } catch (_) {}
        if (!batches || Array.isArray(batches) || typeof batches !== 'object') batches = {};
        batchStorageKeyRef.current = batchStorageKey;
        uploadsDispatch({type: 'restoreCurrentUploadBatches', batches, scope: batchStorageKey});
        return () => { batchStorageKeyRef.current = null; };
    }, [batchStorageKey]);
    useEffect(() => {
        if (currentBatchScope !== batchStorageKey) return;
        try { sessionStorage.setItem(batchStorageKey, JSON.stringify(currentBatches)); } catch (_) {}
    }, [currentBatches, currentBatchScope, batchStorageKey]);

    const refreshFinishedBatches = useRef(true);
    const batchRefreshScheduler = useRequestRefreshScheduler(async () => {
        const storageKey = batchStorageKey;
        const refreshAll = refreshFinishedBatches.current;
        refreshFinishedBatches.current = false;
        await Promise.all(Object.values(batchesRef.current).filter(batch => !batch.deleted && (refreshAll || batchNeedsRefresh(batch))).map(async batch => {
            try {
                const {data} = await api(`/api/bulk_uploads/${batch.id}/status`);
                if (batchStorageKeyRef.current === storageKey) {
                    uploadsDispatch({type: 'currentUploadBatchStatus', id: batch.id, status: data});
                }
            } catch (error) {
                if (error.response?.status !== 404) throw error;
                if (batchStorageKeyRef.current === storageKey) {
                    uploadsDispatch({type: 'currentUploadBatchDeleted', id: batch.id});
                }
            }
        }));
    }, {delay: 500, debounce: false, deferWhenHidden: true, refreshKey: batchStorageKey,
        onError: error => console.warn('Current upload status unavailable:', error)});
    const refreshDisplayedBatches = () => {
        refreshFinishedBatches.current = true;
        const refresh = batchRefreshScheduler.schedulePromise();
        // Fire-and-forget callers use the scheduler's onError handler; keep the
        // original rejection available to dataRefresh's waitUntil contract.
        refresh.catch(() => {});
        return refresh;
    };
    useEffect(() => {
        refreshDisplayedBatches();
        const interval = setInterval(() => batchRefreshScheduler.schedule(), 5000);
        // Terminal batches still change when files are trashed or restored.
        // This bounded fallback only reconciles batches already in this tab.
        const finishedInterval = setInterval(refreshDisplayedBatches, 60000);
        return () => {clearInterval(interval); clearInterval(finishedInterval);};
    }, [batchStorageKey, Object.keys(currentBatches).join(',')]);
    useEffect(() => { refreshDisplayedBatches(); }, [assetEdits]);
    useEffect(() => {
        const onRefresh = event => {
            if (!event.detail?.scopes?.some(scope => ['upload_status', 'upload_counts', 'assets'].includes(scope))) return;
            const refresh = refreshDisplayedBatches();
            event.detail?.waitUntil?.(refresh, ['upload_status']);
        };
        window.addEventListener('mediagraph:dataRefresh', onRefresh);
        return () => window.removeEventListener('mediagraph:dataRefresh', onRefresh);
    }, [batchStorageKey]);

    // Keep uploadStateRef in sync with uploadState
    useEffect(() => {
        if(currentUpload?.guid) uploadStateRef.current.byGuid[currentUpload.guid] = uploadState

        uploadStateRef.current = {
            ...uploadStateRef.current,
            state: uploadState,
            uploadId: currentUpload?.id,
            uploadGuid: currentUpload?.guid
        };
    }, [uploadState, currentUpload?.id, currentUpload?.guid]);

    const stateForUpload = (upload) => {
        const currentState = uploadStateRef.current
        const stateByGuid = upload?.guid && currentState.byGuid[upload.guid]
        const matchesId = upload?.id && currentState.uploadId === upload.id
        const matchesGuid = upload?.guid && currentState.uploadGuid === upload.guid

        if(stateByGuid === 'cancelled') return stateByGuid
        if(pausedUploadGuids.current.has(upload?.guid)) return 'paused'
        if(stateByGuid) return stateByGuid

        return matchesId || matchesGuid ? currentState.state : 'active'
    }

    const uploadStatsRefreshScheduler = useRequestRefreshScheduler(() => {
        if(currentUpload?.guid) {
            return api(`/api/uploads/${currentUpload.guid}`).then(res => {
                assetGroupDispatch({type: 'setCurrentUpload', upload: res.data});
            });
        }
    }, {
        onError: error => console.error('Failed to refresh upload stats:', error),
        debounce: false,
    });

    const refreshUploadStats = () => uploadStatsRefreshScheduler.schedule();

    const onProgress = (asset, percent, stats) => {
        uploadsDispatch({type:'uploadProgress', asset, percent, stats})
        // Evaporate emits progress ticks even when no bytes are moving.
        const bytes = stats?.totalUploaded ?? percent * (asset.file_size || 0);
        if(bytes > (transferredBytesByAsset.current[asset.id] || 0)) {
            lastProgressTimestamps.current[asset.id] = Date.now();
            uploadsDispatch({type: 'updateUploadProgress', assetId: asset.id});
        }
        // Aborted multipart requests roll their byte count back. The next
        // positive movement is real activity even below the old high-water mark.
        transferredBytesByAsset.current[asset.id] = bytes;

        // Throttled stats refresh during progress
        const now = Date.now();
        if (now - lastStatsRefresh.current > STATS_REFRESH_THROTTLE) {
            lastStatsRefresh.current = now;
            refreshUploadStats();
        }
    }

    const consumer = useConsumer();
    const sub = useRef();
    const bulkUploadLifecycle = useRef(null);

    useEffect(() => {
        const lifecycle = createBulkUploadLifecycle()
        bulkUploadLifecycle.current = lifecycle

        return () => lifecycle.dispose()
    }, [])

    const uploadAsset = (asset, cb) => {
        let released = false;
        const releaseSlot = () => { if(!released) { released = true; cb?.(); } };
        const assetUploadState = stateForUpload(asset.upload)
        console.log('uploadAsset called for:', asset.id, 'Upload state:', assetUploadState)

        if (shouldStopForCancellation(assetUploadState, asset.allowed_after_cancellation)) {
            console.log('Upload is cancelled, skipping asset:', asset.id);
            releaseSlot();
            return;
        }

        // If upload is paused, wait and retry
        if (assetUploadState === 'paused' || offlineRef.current) {
            console.log('Upload is paused, waiting before adding asset:', asset.id);
            setTimeout(() => uploadAsset(asset, cb), 1000);
            return;
        }

        console.log('adding to evaporate:', asset)
        const file = _.find(FileQueue[asset.upload.guid], {upload_guid: asset.upload_guid})

        if(!file) {
            uploadsDispatch({type: 'markAssetStalled', assetId: asset.id});
            releaseSlot();
            return;
        }

        const evaporateId = decodeURIComponent(`${Config.uploadBucket}/${asset.s3_upload_key}`)
        evaporateIdsByUploadGuid.current[asset.upload.guid] ||= new Set()
        evaporateIdsByUploadGuid.current[asset.upload.guid].add(evaporateId)

        uploader.current.then((evaporate) => {
            evaporate.add({
                file: file,
                name: asset.s3_upload_key,
                progress: (percent, stats) => onProgress(asset, percent, stats),
                complete: (xhr, awsObjectKey) => {
                    console.log('Complete!', awsObjectKey)
                    refreshUploadStats();
                    releaseSlot()
                },
                error: (msg) => {
                    console.log('Evaporate Error:', msg)

                    // Evaporate retries recoverable requests itself. Calling resume
                    // on an active file rejects; explicit recovery pauses it first.
                },
                paused: () => console.log('Paused'),
                pausing: () => console.log('Pausing'),
                resumed: () => {
                    console.log('Resumed')
                    // Clear stalled status on successful resume
                    uploadsDispatch({type: 'updateUploadProgress', assetId: asset.id})
                    delete lastProgressTimestamps.current[asset.id];
                },
                cancelled: () => console.log('Cancelled'),
                started: key => {
                    lastProgressTimestamps.current[asset.id] = Date.now();
                    uploadsDispatch({type: 'uploadTransferStarted', assetId: asset.id});
                    api.post(`/api/assets/started_upload`, {key}).catch(error => console.warn('Upload start notification unavailable', error));
                },
                uploadInitiated: () => {
                    assetsDispatch({type:'updateAsset', asset: {...asset, aasm_state:'uploading'}})
                    refreshUploadStats();
                },
                warn: (msg) => {
                    console.log('Evaporate Warning:', msg)
                }
            }).then(
                () => {
                    evaporateIdsByUploadGuid.current[asset.upload.guid]?.delete(evaporateId)
                    onFinish(asset)
                },
                (reason) => {
                    evaporateIdsByUploadGuid.current[asset.upload.guid]?.delete(evaporateId)
                    console.log('File did not upload successfully:', reason)
                    releaseSlot();

                    if(shouldStopForCancellation(stateForUpload(asset.upload), asset.allowed_after_cancellation)) return

                    // Mark as stalled on failure
                    uploadsDispatch({type: 'markAssetStalled', assetId: asset.id});
                    refreshUploadStats();
                }
            );
        });
    }
    // TODO: useEffect for new version uploads to pass to `uploadAsset`

    //--------------------------------
    // Setup ActionCable Subscription:
    //--------------------------------

    const {t} = useTranslation();
    const preparingUploadGuids = useRef(new Set())
    const bulkUploadGuids = useRef({})
    const uploadCounts = useRef({})
    const totalUploads = useRef(0)

    const isUploadTracked = useCallback((uploadGuid) => {
        return Boolean(
            uploadGuid &&
            (preparingUploadGuids.current.has(uploadGuid) || uploadCounts.current[uploadGuid] > 0)
        )
    }, [])

    const clearUploadTracking = useCallback((uploadGuid) => {
        if(!uploadGuid) return

        totalUploads.current = Math.max(0, totalUploads.current - (uploadCounts.current[uploadGuid] || 0))
        delete uploadCounts.current[uploadGuid]
        preparingUploadGuids.current.delete(uploadGuid)
        delete evaporateIdsByUploadGuid.current[uploadGuid]
        cancelledRevisionUploadGuids.current.delete(uploadGuid)
        pausedUploadGuids.current.delete(uploadGuid);
        uploadingAssetsRef.current.filter(asset => asset.upload?.guid === uploadGuid).forEach(asset => {
            pendingFinalizations.current.delete(asset.id);
            delete lastProgressTimestamps.current[asset.id];
        });
        uploadsDispatch({type: 'clearUploadingAssets', uploadGuid})

        Object.keys(bulkUploadGuids.current).forEach(id => {
            if(bulkUploadGuids.current[id] === uploadGuid) {
                bulkUploadLifecycle.current?.cancel(Number(id))
                cancelledRevisionBulkUploadIds.current.delete(Number(id))
                delete bulkUploadGuids.current[id]
            }
        })
    }, [uploadsDispatch])

    const onError = (data, lifecycle = bulkUploadLifecycle.current)=> {
        if(!lifecycle.claimError(data.id)) return

        const uploadGuid = bulkUploadGuids.current[data.id]
        preparingUploadGuids.current.delete(uploadGuid)
        cancelledRevisionBulkUploadIds.current.delete(data.id)
        cancelledRevisionUploadGuids.current.delete(uploadGuid)
        delete bulkUploadGuids.current[data.id]

        message.error({
            content: data.message || t('bulk-upload-prepare-error', 'No uploadable files could be prepared.'),
            duration: 8
        })
    }

    const onReady = (data, source, lifecycle = bulkUploadLifecycle.current)=> {
        if(!lifecycle.claimReady(data.id)) return

        const uploadGuid = bulkUploadGuids.current[data.id]
        const cancelledRevision = cancelledRevisionBulkUploadIds.current.has(data.id)

        if(shouldStopForCancellation(stateForUpload({guid: uploadGuid}), cancelledRevision)) {
            clearUploadTracking(uploadGuid)
            return
        }

        // onReady {type: "ready", id: 1305, total: 0} cable
        console.log('onReady', data, source)

        if(data.existing > 0) {
            notification.warning({
                message: t('existing-assets-skipped-message', 'Skipping existing Assets'),
                description: (
                    <VerticalSpace>
                        <div>{`${data.existing} ${t('existing-assets-notice', 'Assets already present in Upload, skipping.')}`}</div>
                        <ExistingAssetsModal id={data.id}/>
                    </VerticalSpace>
                ),
                duration: 0
            })
        }

        const per_page = 50

        let assets = []
        const getPage = (page)=> {
            if(!lifecycle.isReady(data.id)) return

            api(`/api/bulk_uploads/${data.id}?page=${page}&per_page=${per_page}`).then(res => {
                if(!lifecycle.isReady(data.id)) return

                const pageUploadGuid = res.data[0]?.upload?.guid || uploadGuid

                if(shouldStopForCancellation(stateForUpload({guid: pageUploadGuid}), cancelledRevision)) {
                    clearUploadTracking(pageUploadGuid)
                    return
                }

                if(res.data.status === 'processing') {
                    const retryTimeout = setTimeout(() => {
                        lifecycle.releaseRetryTimeout(data.id, retryTimeout)
                        getPage(1)
                    }, 500)
                    lifecycle.trackRetryTimeout(data.id, retryTimeout)
                    return
                }

                assets = assets.concat(res.data)
                totalUploads.current += res.data.length
                uploadCounts.current[pageUploadGuid] = (uploadCounts.current[pageUploadGuid] || 0) + res.data.length

                assetsDispatch({type: 'assetsAdded', assets: res.data});
                loadedAssetsDispatch({type: 'assetsAdded', assets: res.data}); // IDs only

                uploadsDispatch({type:'addUploadingAssets', assets: res.data})

                if(res.headers['total-pages'] > page) {
                    getPage(page + 1)

                } else {
                    lifecycle.completeReady(data.id)
                    preparingUploadGuids.current.delete(pageUploadGuid)
                    cancelledRevisionBulkUploadIds.current.delete(data.id)
                    delete bulkUploadGuids.current[data.id]
                    mapLimit(assets, 5, uploadAsset)
                }
            })
        }

        aggsDispatch({type:'setTotal', total: data.total})

        getPage(1)
    }

    useEffect(()=>{
        const lifecycle = bulkUploadLifecycle.current

        if(!sub.current && sessionGuid && currentOrg && consumer && consumer.subscriptions) {
            console.log('connecting to BulkUploadsChannel: ', sessionGuid, currentOrg.id, sub.current)

            sub.current = consumer.subscriptions.create({channel: "BulkUploadsChannel", guid: sessionGuid}, {
                connected: ()=> {
                    console.log('BulkUploadsChannel connected')
                },
                disconnected: ()=> {
                    console.log('BulkUploadsChannel disconnected!')
                },
                received: (data) => {
                    switch(data.type) {
                        case 'ready':
                            console.log('BulkUploadsChannel: ready')
                            onReady(data, 'cable', lifecycle)
                            return

                        case 'error':
                            console.log('BulkUploadsChannel: error')
                            onError(data, lifecycle)
                            return

                        case 'retryUpload':
                            console.log('RETRYING UPLOAD:', data.asset)
                            uploadAsset(data.asset)
                            return

                        case 'progress':
                            if(!lifecycle.isPreparing(data.id)) return

                            lifecycle.setCloseMessage(data.id, message.open({
                                key: `preparing-${data.id}`,
                                type: 'loading',
                                content: <PreparingUploadMessage count={data.total} progress={data.progress}/>,
                                duration: 0
                            }))
                            return
                    }
                }
            });
        }

        return ()=> {
            console.log('UploadManager unloaded')
            clearBulkUploadSubscription(consumer, sub)
        }
    }, [currentOrg?.id, consumer])

    //---------------------
    // Prepare Bulk Upload:
    //---------------------

    useEffect(()=>{
        const lifecycle = bulkUploadLifecycle.current

        if(disconnected) {
            message.error('Please wait until reconnected before uploading');
            return
        }

        // POST entire file list to api w/ upload_guid, queue background job, send asset records to front end via cable:
        Object.keys(uploads).map((guid)=> {
            if(guid == 'new') return;

            const files = uploads[guid]

            uploadsDispatch({type:'filesProcessed', files, guid})

            const assets = _.compact(files.map(file => {
                // .filename can be manually set to override; e.g. <DokaButton/>

                // Replace narrow-space character for S3 / CloudFront compatibility:
                let filename = (file.filename || file.name).replace(/\u202F/g, ' ')
                let path = file.webkitRelativePath

                if(filename.match(/\//)) {
                    filename = _.last(file.name.split(/\//));
                    path = file.name
                }

                if(file.upload_guid || file.size == 0 || filename.match(/(\/|^)\./) || path.match(/(\/|^)\./)) return;

                file.upload_guid = uuid()

                return {
                    filename, path, file_size: file.size, upload_guid: file.upload_guid,
                    new_version: file.new_version, asset_id: file.asset_id
                };
            }))

            if(assets.length) {
                preparingUploadGuids.current.add(guid)

                const blob = new Blob([JSON.stringify(assets)], {
                    type: 'application/json'
                });
                const formData = new FormData()
                formData.append('assets', blob)

                api.post(`/api/uploads/${guid}/assets?session_guid=${sessionGuid}`, formData, {
                    headers: {'Content-Type': 'multipart/form-data'}
                }).then(res => {
                    const id = res.data.bulk_upload_id
                    bulkUploadGuids.current[id] = guid
                    uploadsDispatch({type: 'beginCurrentUploadBatch', id, uploadGuid: guid, files: assets});

                    if(res.data.cancelled_revision) {
                        cancelledRevisionBulkUploadIds.current.add(id)
                        cancelledRevisionUploadGuids.current.add(guid)
                    }

                    if(shouldStopForCancellation(stateForUpload({guid}), res.data.cancelled_revision)) {
                        clearUploadTracking(guid)
                        return
                    }

                    const closeMessage = message.open({
                        key: `preparing-${id}`,
                        type: 'loading',
                        content: <PreparingUploadMessage count={res.data.count} progress={0}/>,
                        duration: 0
                    })

                    // Failsafe interval in case `ready` message missed
                    const pollStatus = ()=> {
                        if(!lifecycle.beginStatusRequest(id)) return

                        api(`/api/bulk_uploads/${id}/status`).then(res => {

                            console.log(`Checking Bulk Upload ${id} status: `, res.data.status)

                            if(res.data.status == 'ready') onReady(res.data, 'poll', lifecycle)
                            if(res.data.status == 'error') onError(res.data, lifecycle)
                        }).finally(() => lifecycle.finishStatusRequest(id))
                    }

                    const intervalId = setInterval(pollStatus, 3_000)
                    lifecycle.start(id, {intervalId, closeMessage, pollNow: pollStatus})
                }).catch(error => {
                    preparingUploadGuids.current.delete(guid)

                    if(error.response?.status === 409) {
                        clearUploadTracking(guid)
                        return
                    }

                    console.error('Failed to prepare upload:', error)
                })
            }
        })
    }, [fileAdds])

    const onFinish = (asset)=>{
        pendingFinalizations.current.set(asset.id, asset);
        if(finalizationRequests.current.has(asset.id)) return;
        finalizationRequests.current.add(asset.id);
        api.get(`/api/assets/${asset.guid}/set_uploaded`).then(res => {
            pendingFinalizations.current.delete(asset.id);
            delete lastProgressTimestamps.current[asset.id];
            delete transferredBytesByAsset.current[asset.id];
            const uploadGuid = asset.upload?.guid

            if(shouldStopForCancellation(stateForUpload(asset.upload), asset.allowed_after_cancellation)) {
                clearUploadTracking(uploadGuid)
                return
            }

            if(uploadCounts.current[uploadGuid] > 0) {
                uploadCounts.current[uploadGuid] -= 1
                totalUploads.current = Math.max(0, totalUploads.current - 1);

                if(uploadCounts.current[uploadGuid] === 0) {
                    delete uploadCounts.current[uploadGuid]
                    cancelledRevisionUploadGuids.current.delete(uploadGuid)
                }
            }

            uploadsDispatch({type: 'removeUploadingAsset', assetId: asset.id})
            uploadsDispatch({type: 'currentUploadTransferFinished', asset})
            batchRefreshScheduler.schedule();

            if(currentUpload?.id === res.data.upload_id) {
                delete res.data.aasm_state // prevent race condition with Sidekiq
                assetsDispatch({type:'updateAsset', asset: res.data})
                editAssetDispatch({type:'increment'})

                // Coalesce completion bursts into one upload-stats refresh.
                refreshUploadStats();
            }
        }).catch(error => {
            if(error.response?.status === 409) {
                clearUploadTracking(asset.upload?.guid)
                return
            }

            console.error('Failed to mark asset uploaded:', error)
        }).finally(() => finalizationRequests.current.delete(asset.id));
    }

    //--------------------------------------
    // Create Evaporate.js Instance For Org:
    //--------------------------------------

    const uploader = useRef();
    window.uploader = uploader;

    useEffect(()=> {
        if(!currentOrg) return;

        const config = {
            logging: false,
            progressIntervalMS: 250,
            signerUrl: `${Config.apiHost}/api/assets/sign`,
            aws_key: Config.awsKey,
            bucket: Config.uploadBucket,
            s3Acceleration: true,
            computeContentMd5: true,
            cryptoMd5Method: (data)=> { btoa(SparkMD5.ArrayBuffer.hash(data, true)) },
            cryptoHexEncodedHash256: sha256,
            signHeaders: {
                'Authorization': `Bearer ${jwt}`,
                'OrganizationId': currentOrg.id
            }
        }

        uploader.current = Evaporate.create(config).then(installEvaporateSchedulerFix);

        const handleBeforeunload = (e)=> {
            console.log('totalUploads:')

            if(totalUploads.current == 0) {
                delete e['returnValue'];
                return true;
            }

            e.preventDefault();
            const msg = `${totalUploads.current} ${pluralize('file', totalUploads.current)} remaining to upload, leaving now will lose them.`
            e.returnValue = msg

            return msg
        }

        window.addEventListener('beforeunload', handleBeforeunload);

        return ()=>{
            window.removeEventListener('beforeunload', handleBeforeunload)
        }
    }, [currentOrg?.id])

    const batches = Object.values(currentBatchScope === batchStorageKey ? currentBatches : {});
    const activeBatches = batches.filter(batch => !batchIsTerminal(batch));
    const totalBytes = batches.reduce((sum, batch) => sum + batch.totalBytes, 0);
    const transferredBytes = batches.reduce((sum, batch) => sum + batchTransferredBytes(batch), 0);
    const hasRemovedFiles = batches.some(batch => batch.removed > 0);
    const totalPercent = totalBytes ? Math.min(100, transferredBytes / totalBytes * 100) : 0;
    const transferring = activeBatches.some(batchIsTransferring);

    const [paused, setPaused] = useState(false)

    const pause = useCallback((uploadGuid) => {
        const evaporateIds = [...(evaporateIdsByUploadGuid.current[uploadGuid] || [])]
        pausedUploadGuids.current.add(uploadGuid);
        uploadsDispatch({type: 'uploadTransferPaused', uploadGuid, paused: true});
        evaporateIds.forEach(id => {
            pauseOperations.current[id] = (pauseOperations.current[id] || Promise.resolve())
                .then(() => uploader.current)
                .then(ev => ev.pause(id, {force: true})).catch(() => {});
        });
        setPaused(true);
    }, []);

    const resume = useCallback((uploadGuid) => {
        const evaporateIds = [...(evaporateIdsByUploadGuid.current[uploadGuid] || [])]
        pausedUploadGuids.current.delete(uploadGuid);
        uploadsDispatch({type: 'uploadTransferPaused', uploadGuid, paused: false});
        evaporateIds.forEach(id => {
            pauseOperations.current[id] = (pauseOperations.current[id] || Promise.resolve())
                .then(() => uploader.current)
                .then(ev => {
                    if(offlineRef.current || pausedUploadGuids.current.has(uploadGuid)) return;
                    return ev.resume(id);
                }).catch(error => console.warn('Upload could not resume', error));
        });
        setPaused(pausedUploadGuids.current.size > 0);
    }, []);

    const pauseTrackedUploads = useCallback(() => {
        activeUploadGuids(uploadCounts.current, evaporateIdsByUploadGuid.current).forEach(pause)
    }, [pause])

    const resumeTrackedUploads = useCallback(() => {
        activeUploadGuids(uploadCounts.current, evaporateIdsByUploadGuid.current).forEach(async guid => {
            const asset = uploadingAssetsRef.current.find(asset => asset.upload?.guid === guid);
            const serverPaused = uploadStateRef.current.byGuid[guid] === 'paused' ||
                Object.values(batchesRef.current).some(batch => batch.uploadGuid === guid && batch.uploadState === 'paused');
            if(serverPaused && asset?.upload?.id) {
                try {
                    const {data} = await api.post(`/api/uploads/${asset.upload.id}/resume`);
                    if(data.upload_state !== 'active') return;
                    uploadStateRef.current.byGuid[guid] = data.upload_state;
                    if(uploadStateRef.current.uploadGuid === guid) uploadsDispatch({type: 'setUploadState', uploadState: data.upload_state});
                } catch(error) {
                    message.error(t('resume-failed', 'Could not resume upload. Please try again.'));
                    return;
                }
            }
            resume(guid);
        });
    }, [resume])

    // Only a server/session state transition controls the transport here.
    // Progress renders must not undo a pause made from the header.
    useEffect(() => {
        console.log('Upload state changed:', uploadState, 'Paused state:', paused);
        const appliesToTrackedUpload = isUploadTracked(currentUpload?.guid)

        const isCancelledRevision = cancelledRevisionUploadGuids.current.has(currentUpload?.guid)

        if (appliesToTrackedUpload && shouldStopForCancellation(uploadState, isCancelledRevision)) {
            pause(currentUpload.guid)
            clearUploadTracking(currentUpload.guid)
        } else if (appliesToTrackedUpload && uploadState === 'paused') {
            console.log('Pausing upload based on context state');
            pause(currentUpload.guid);
        } else if (appliesToTrackedUpload && uploadState === 'active' && pausedUploadGuids.current.has(currentUpload.guid)) {
            console.log('Resuming upload based on context state');
            resume(currentUpload.guid);
        }
    }, [
        uploadState,
        currentUpload?.guid,
        pause,
        resume,
        isUploadTracked,
        clearUploadTracking
    ]);

    const recoverTransfers = useRef();
    recoverTransfers.current = (uploadGuid, resumePaused = false) => {
        if(offlineRef.current) return;
        if(resumePaused) {
            pausedUploadGuids.current.delete(uploadGuid);
            uploadsDispatch({type: 'uploadTransferPaused', uploadGuid, paused: false});
            setPaused(pausedUploadGuids.current.size > 0);
        }
        uploadingAssetsRef.current.filter(asset => !uploadGuid || asset.upload?.guid === uploadGuid).forEach(asset => {
            const state = stateForUpload(asset.upload);
            if(state === 'paused' || shouldStopForCancellation(state, asset.allowed_after_cancellation)) return;
            if(pendingFinalizations.current.has(asset.id)) {
                onFinish(asset);
                return;
            }
            const id = decodeURIComponent(`${Config.uploadBucket}/${asset.s3_upload_key}`);
            if(!evaporateIdsByUploadGuid.current[asset.upload.guid]?.has(id)) {
                if(stalledAssetsRef.current[asset.id]) uploadAsset(asset);
                return;
            }
            pauseOperations.current[id] = (pauseOperations.current[id] || Promise.resolve())
                .then(() => uploader.current)
                .then(async ev => {
                    // Recover an active but stuck request, not only PAUSED files.
                    await ev.pause(id, {force: true}).catch(() => {});
                    const latestState = stateForUpload(asset.upload);
                    if(offlineRef.current || latestState === 'paused' || shouldStopForCancellation(latestState, asset.allowed_after_cancellation)) return;
                    await ev.resume(id);
                    uploadsDispatch({type: 'updateUploadProgress', assetId: asset.id});
                }).catch(error => console.warn('Upload recovery failed', error));
        });
        refreshUploadStats();
        batchRefreshScheduler.schedule();
    };

    useEffect(() => {
        if(transferRetry?.guid) recoverTransfers.current(transferRetry.guid, true);
    }, [transferRetry]);

    useEffect(() => {
        const onOffline = () => {
            offlineRef.current = true;
            uploadsDispatch({type: 'setUploadNetworkOffline', offline: true});
        };
        const onOnline = () => {
            offlineRef.current = false;
            uploadsDispatch({type: 'setUploadNetworkOffline', offline: false});
            recoverTransfers.current();
        };
        if(navigator.onLine === false) onOffline();
        window.addEventListener('offline', onOffline);
        window.addEventListener('online', onOnline);
        return () => {
            window.removeEventListener('offline', onOffline);
            window.removeEventListener('online', onOnline);
        };
    }, []);

    // Stalled detection
    useEffect(() => {
        const stalledCheckInterval = setInterval(() => {
            const now = Date.now();

            uploadingAssetsRef.current.forEach(asset => {
                const lastProgress = lastProgressTimestamps.current[asset.id];

                // Check if asset is uploading but hasn't made progress
                if (lastProgress != null && stateForUpload(asset.upload) !== 'paused' && !stalledAssetsRef.current[asset.id]) {
                    const timeSinceProgress = now - lastProgress;

                    if (timeSinceProgress > STALL_TIMEOUT) {
                        console.log(`Asset ${asset.id} stalled - no progress for ${timeSinceProgress}ms`);

                        // Mark as stalled in context
                        uploadsDispatch({type: 'markAssetStalled', assetId: asset.id});

                        // This is a browser transfer observation, not proof that
                        // server-side processing has stalled. Keep it local.
                    }
                }
            });
        }, 10000); // Check every 10 seconds

        return () => clearInterval(stalledCheckInterval);
    }, []);

    const batchDetails = <UploadBatchDetails organizationId={currentOrg?.id}/>;
    if(!batches.length) return batchDetails;

    if(isMobile() || quickUpload) return batchDetails;

    return (
        <>{batchDetails}<Popover
            onOpenChange={open => {if (open) refreshDisplayedBatches();}}
            title={t('current-uploads', 'Current uploads')}
            overlayStyle={{maxWidth:400}}
            content={
                <>
                    {networkOffline && <div role="status">{t('upload-offline', 'Offline — reconnect to continue uploading. Server processing may continue.')}</div>}
                    {!networkOffline && Object.keys(stalledAssets).length > 0 && <div role="status">
                        {t('upload-no-transfer-progress', 'No transfer progress for 60 seconds. Check your connection and resume.')}
                        <Button onClick={() => recoverTransfers.current()}>{t('resume-uploads', 'Resume uploads')}</Button>
                    </div>}
                    {batches.map(batch => (
                        <div key={batch.id} style={{marginBottom: 12}}>
                            <strong>{batch.name || t('upload-batch', 'Upload batch {{id}}', {id: batch.id})}</strong>
                            <div>{batchIsDeleted(batch) ? t('upload-deleted', 'Upload Deleted') : <UploadBatchLink id={batch.id}/>}</div>
                            <div>{batch.canceled ? t('cancelled', 'Cancelled') :
                                batch.preparationError ? t('upload-preparation-failed', 'Preparation failed') :
                                    !batch.prepared ? t('preparing-upload', 'Preparing files') :
                                        batch.uploadState === 'paused' ? t('paused', 'Paused') :
                                            batch.uploadState === 'stalled' ? t('stalled', 'Stalled') : null}</div>
                            {!batch.removed && <><div>{t('upload-bytes-transferred', '{{done}} of {{total}} transferred', {
                                done: filesize(batchTransferredBytes(batch)), total: filesize(batch.totalBytes),
                            })}</div>
                            <Progress percent={batch.totalBytes ? Math.round(batchTransferredBytes(batch) / batch.totalBytes * 100) : 0}/></>}
                            <div>{t('upload-files-processed', '{{done}} of {{total}} files processed', {done: batch.completed, total: batch.total})}
                                {batch.failed > 0 && ` · ${t('upload-files-failed', '{{count}} failed', {count: batch.failed})}`}
                                {batch.removed > 0 && ` · ${t('upload-files-removed', '{{count}} removed', {count: batch.removed})}`}</div>
                        </div>
                    ))}
                    {transferring && !paused && (
                        <Button onClick={pauseTrackedUploads} icon={<PauseOutlined/>}>
                            Pause {totalUploads.current} {pluralize('Upload', totalUploads.current)}
                        </Button>
                    ) || (transferring && (
                        <Button onClick={resumeTrackedUploads} icon={<PlayCircleOutlined/>}>
                            Resume {totalUploads.current} {pluralize('Upload', totalUploads.current)}
                        </Button>
                    ))}
                    {/*{uploadingAssets.map(asset => (*/}
                    {/*    <div key={asset.id}>*/}
                    {/*        <small>{asset.filename}</small>*/}
                    {/*        <Progress percent={(asset.percent * 100).toFixed()} size="small" status="active" />*/}
                    {/*    </div>*/}
                    {/*))}*/}
                </>
            }
        >
            <Button type={'text'} aria-label={t('current-uploads', 'Current uploads')}
                    style={{color: currentOrg?.header_text_color || '#fff'}}>
                {transferring && hasRemovedFiles ? t('uploads-transferring', 'Uploading files') : transferring ? <Progress
                    type="circle"
                    status={'active'}
                    percent={totalPercent}
                    width={40}
                    format={p => <span style={{color:currentOrg?.header_text_color || '#fff'}}>{p.toFixed()}%</span>}
                /> : activeBatches.length ? t('uploads-processing', 'Processing {{count}} files', {
                    count: activeBatches.reduce((sum, batch) => sum + Math.max(0, batch.total - batch.completed - batch.failed - (batch.removed || 0)), 0),
                }) : batches.some(batch => batch.failed || batch.preparationError) ?
                    t('uploads-have-errors', 'Uploads need attention') :
                    batches.every(batch => batch.total > 0 && batch.removed === batch.total) ? t('uploads-removed', 'Uploads removed') :
                    batches.every(batch => batch.canceled) ? t('uploads-cancelled', 'Uploads cancelled') :
                        t('uploads-finished', 'Uploads finished')}
            </Button>
        </Popover></>
    )
}

const PreparingUploadMessage = ({count, progress})=>{
    const {t} = useTranslation();
    return (
        <>
            {count === 1 ? t('preparing-upload-file','Preparing upload of the file') : t('preparing-upload-files','Preparing upload of {{count}} files', {count})}
            {progress < 1 && (
                <>
                    <br/>
                    <Progress percent={Math.round(progress * 100)} status={'active'}/>
                </>
            )}
        </>
    )
}

const ExistingAssetsModal = ({id})=>{
    const {t} = useTranslation();

    const [open, setOpen] = useState()
    const [loading, setLoading] = useState()
    const [filenames, setFilenames] = useState()

    useEffect(() => {
        if(!open) return;

        setLoading(true)
        api(`/api/bulk_uploads/${id}/existing_assets`).then(res => {
            setLoading(false)
            setFilenames(res.data)
        })
    }, [open, id]);

    return (
        <>
            <Button icon={<OrderedListOutlined/>} block onClick={()=> setOpen(true)}>{t('view', 'View')}</Button>
            <Modal
                open={open}
                onCancel={()=> setOpen(false)}
                footer={null}
                title={t('existing-assets','Existing Assets')}
            >
                <List
                    dataSource={filenames}
                    loading={loading}
                    bordered
                    size={'small'}
                    renderItem={filename => <List.Item><FileFilled/> {filename}</List.Item>}
                />
            </Modal>
        </>
    )

}
